/**
 * Ask the service worker to keep what this page has loaded but the worker
 * does not precache: the translation bundles (`CACHE_LOCALES`) and the code of
 * online-only screens and the Arabic font (`CACHE_ASSETS`). Covers the first
 * visit, when the worker is installed but does not control the page yet, so
 * its fetch handler never saw those files; once it controls the page it caches
 * them itself. Best effort: without a worker, a screen needs a connection the
 * first time it is opened.
 */
const seen = new Set<string>();
let observer: PerformanceObserver | null = null;

/**
 * Remember every file this page fetches. `performance.getEntriesByType` alone
 * is not enough: the resource timing buffer holds 250 entries by default and
 * the boot files fill it, so later screens' code would be missed.
 */
export function trackLoadedAssets(): void {
  if (typeof performance === 'undefined' || typeof PerformanceObserver === 'undefined') return;
  try {
    performance.setResourceTimingBufferSize?.(2000);
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) seen.add(entry.name);
    });
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    // Older browsers: fall back to whatever the timing buffer kept.
  }
}

function loadedAssets(): { locales: string[]; assets: string[] } {
  const locales: string[] = [];
  const assets: string[] = [];
  // Entries not yet delivered to the observer's callback (just fetched).
  for (const entry of observer?.takeRecords() ?? []) seen.add(entry.name);
  const names = new Set(seen);
  for (const entry of performance.getEntriesByType('resource')) names.add(entry.name);
  for (const name of names) {
    let pathname: string;
    try {
      const url = new URL(name);
      if (url.origin !== window.location.origin) continue;
      pathname = url.pathname;
    } catch {
      continue;
    }
    if (pathname.startsWith('/assets/locale-')) locales.push(name);
    else if (/^\/assets\/[^/]+\.(?:js|css|woff2)$/.test(pathname)) assets.push(name);
  }
  return { locales, assets };
}

export function cacheLoadedAssets(): void {
  if (import.meta.env.DEV || typeof navigator === 'undefined') return;
  if (!('serviceWorker' in navigator) || typeof performance === 'undefined') return;
  const { locales, assets } = loadedAssets();
  // A controlled page's fetches went through the worker, which cached them.
  const uncontrolled = !navigator.serviceWorker.controller;
  if (!locales.length && !(uncontrolled && assets.length)) return;
  void navigator.serviceWorker.ready
    .then((registration) => {
      if (locales.length)
        registration.active?.postMessage({ type: 'CACHE_LOCALES', urls: locales });
      if (uncontrolled && assets.length)
        registration.active?.postMessage({ type: 'CACHE_ASSETS', urls: assets });
    })
    .catch(() => undefined);
}
